import React, { useState } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../../ui/dialog';
import { Button } from '../../ui/button';
import { Badge } from '../../ui/badge';
import { Ship, Package, Wrench, Layers, User, Building2, Calendar, Clock, Download, Send, Edit, CheckCircle2, XCircle, Eye, GitCompare } from 'lucide-react';
import { formatCurrency, formatDate } from '../crmUtils';
import { STATUS_CONFIG } from './constants';
import { VersaoCompareModal } from './VersaoCompareModal';

export const PropostaViewModal = ({ viewProposta, setViewProposta, downloadPdf, previewPdf, changeStatus, openEdit }) => {
  const [compareVersion, setCompareVersion] = useState(null);

  if (!viewProposta) return null;

  const versoes = viewProposta.versoes_anteriores || [];
  const versaoAtual = viewProposta.versao || 1;

  return (
    <>
      <Dialog open={!!viewProposta} onOpenChange={() => setViewProposta(null)}>
        <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-3">
              <span>Proposta {viewProposta.numero}</span>
              {versaoAtual > 1 && <Badge variant="outline" className="text-[10px] bg-indigo-50 text-indigo-600 border-indigo-200">v{versaoAtual}</Badge>}
              <Badge variant="outline" className={STATUS_CONFIG[viewProposta.status]?.cor}>{STATUS_CONFIG[viewProposta.status]?.label}</Badge>
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4 mt-2">
            <div className="grid grid-cols-2 gap-3 text-sm">
              <div className="flex items-center gap-2"><User className="w-4 h-4 text-slate-400" /><div><p className="text-xs text-slate-500">Cliente</p><p className="font-medium">{viewProposta.cliente_nome}</p>{viewProposta.cliente_cnpj && <p className="text-xs text-slate-400">{viewProposta.cliente_cnpj}</p>}</div></div>
              <div className="flex items-center gap-2"><Building2 className="w-4 h-4 text-slate-400" /><div><p className="text-xs text-slate-500">Vendedor</p><p className="font-medium">{viewProposta.vendedor_nome}</p></div></div>
              <div className="flex items-center gap-2"><Calendar className="w-4 h-4 text-slate-400" /><div><p className="text-xs text-slate-500">Criação</p><p className="font-medium">{formatDate(viewProposta.created_at)}</p></div></div>
              <div className="flex items-center gap-2"><Clock className="w-4 h-4 text-slate-400" /><div><p className="text-xs text-slate-500">Validade</p><p className="font-medium">{formatDate(viewProposta.data_validade)}</p></div></div>
            </div>
            {viewProposta.empresa_nome && (
              <div className="text-xs bg-slate-50 rounded-lg px-3 py-2 border border-slate-100">
                <span className="text-slate-400">Empresa Emissora: </span>
                <span className="font-medium text-slate-700">{viewProposta.empresa_nome}</span>
              </div>
            )}
            <div>
              <h3 className="text-sm font-semibold text-slate-600 mb-2">Itens</h3>
              <div className="space-y-2">
                {(viewProposta.itens || []).map((item, i) => (
                  <div key={i} className="bg-slate-50 rounded-lg px-3 py-2 text-sm">
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-2">
                        {item.tipo === 'embarcacao' && <Ship className="w-4 h-4 text-blue-500" />}
                        {item.tipo === 'produto' && <Package className="w-4 h-4 text-emerald-500" />}
                        {item.tipo === 'servico' && <Wrench className="w-4 h-4 text-amber-500" />}
                        {item.tipo === 'composicao' && <Layers className="w-4 h-4 text-purple-500" />}
                        <span className="font-medium">{item.vessel_model_nome || item.produto_nome || item.descricao}</span>
                        {item.quantidade > 1 && <span className="text-slate-400">x{item.quantidade}</span>}
                        {item.lote && <Badge variant="outline" className="text-[9px] bg-indigo-50 text-indigo-500 border-indigo-200">{item.lote}</Badge>}
                        {item.unidade && item.unidade !== 'UN' && <Badge variant="outline" className="text-[9px]">{item.unidade}</Badge>}
                      </div>
                      <span className="font-semibold">{formatCurrency(item.subtotal)}</span>
                    </div>
                    {item.tipo === 'embarcacao' && (
                      <div className="mt-1 pl-6 space-y-0.5">
                        {(item.upgrades_selecionados || []).map((u, j) => (
                          <div key={j} className="text-xs text-amber-600">Upgrade: {u.descricao} (+{formatCurrency(u.preco_adicional)})</div>
                        ))}
                        {(item.opcionais_selecionados || []).map((o, j) => (
                          <div key={j} className="text-xs text-blue-600">Opcional: {o.descricao} (+{formatCurrency(o.preco)})</div>
                        ))}
                      </div>
                    )}
                    {item.tipo === 'composicao' && (item.sub_itens || []).length > 0 && (
                      <div className="mt-1 pl-6 space-y-0.5">
                        {item.sub_itens.map((si, j) => (
                          <div key={j} className="text-xs text-purple-600">- {si.produto_nome || 'Item'} x{si.quantidade || 1}</div>
                        ))}
                      </div>
                    )}
                  </div>
                ))}
              </div>
            </div>
            <div className="bg-slate-50 rounded-lg p-3 space-y-1 text-sm">
              <div className="flex justify-between text-slate-600"><span>Subtotal:</span><span>{formatCurrency(viewProposta.subtotal)}</span></div>
              {viewProposta.desconto_calculado > 0 && <div className="flex justify-between text-green-600"><span>Desconto:</span><span>-{formatCurrency(viewProposta.desconto_calculado)}</span></div>}
              <div className="flex justify-between font-bold text-lg border-t border-slate-200 pt-1"><span>Total:</span><span>{formatCurrency(viewProposta.total)}</span></div>
            </div>

            {/* Versões anteriores */}
            {versoes.length > 0 && (
              <div className="border border-indigo-100 rounded-lg p-3 bg-indigo-50/30">
                <div className="flex items-center gap-2 mb-2">
                  <GitCompare className="w-4 h-4 text-indigo-500" />
                  <span className="text-xs font-semibold text-indigo-700">Versões ({versoes.length + 1} total)</span>
                </div>
                <div className="space-y-1.5">
                  {versoes.slice().reverse().map((v, i) => (
                    <div key={i} className="flex items-center justify-between bg-white rounded px-3 py-1.5 text-xs border border-indigo-100">
                      <div className="flex items-center gap-2">
                        <Badge variant="outline" className="text-[9px]">v{v.versao}</Badge>
                        <span className="text-slate-500">{v.data ? new Date(v.data).toLocaleDateString('pt-BR') : '-'}</span>
                        <span className="text-slate-400">|</span>
                        <span className="text-slate-600">{v.itens?.length || 0} itens</span>
                        <span className="font-medium">{formatCurrency(v.total)}</span>
                      </div>
                      <Button
                        variant="ghost" size="sm"
                        className="h-6 text-[10px] px-2 gap-1 text-indigo-600 hover:text-indigo-800"
                        data-testid={`compare-version-${v.versao}`}
                        onClick={() => setCompareVersion(v)}
                      >
                        <GitCompare className="w-3 h-3" /> Comparar
                      </Button>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Histórico */}
            {(viewProposta.historico || []).length > 0 && (
              <details className="text-xs">
                <summary className="cursor-pointer text-slate-500 hover:text-slate-700 font-medium">Histórico ({viewProposta.historico.length} registros)</summary>
                <div className="mt-2 space-y-1 pl-2 border-l-2 border-slate-200">
                  {viewProposta.historico.slice().reverse().map((h, i) => (
                    <div key={i} className="text-slate-500">
                      <span className="text-slate-400">{h.data ? new Date(h.data).toLocaleString('pt-BR') : '-'}</span>
                      {' - '}<span className="font-medium">{h.descricao}</span>
                      {h.usuario_nome && <span className="text-slate-400"> por {h.usuario_nome}</span>}
                    </div>
                  ))}
                </div>
              </details>
            )}

            <div className="flex flex-wrap gap-2 pt-2">
              {previewPdf && (
                <Button data-testid="preview-pdf-btn" variant="outline" size="sm" className="gap-1" onClick={() => previewPdf(viewProposta)}>
                  <Eye className="w-4 h-4" /> Preview PDF
                </Button>
              )}
              <Button data-testid="download-pdf-btn" variant="outline" size="sm" className="gap-1" onClick={() => downloadPdf(viewProposta)}><Download className="w-4 h-4" /> Baixar PDF</Button>
              {viewProposta.status === 'rascunho' && (
                <>
                  <Button size="sm" className="gap-1 bg-blue-600 hover:bg-blue-700" onClick={() => changeStatus(viewProposta, 'enviada')}><Send className="w-4 h-4" /> Marcar como Enviada</Button>
                  <Button variant="outline" size="sm" className="gap-1" onClick={() => { setViewProposta(null); openEdit(viewProposta); }}><Edit className="w-4 h-4" /> Editar</Button>
                </>
              )}
              {viewProposta.status === 'enviada' && (
                <>
                  <Button size="sm" className="gap-1 bg-green-600 hover:bg-green-700" onClick={() => changeStatus(viewProposta, 'aceita')}><CheckCircle2 className="w-4 h-4" /> Aceita</Button>
                  <Button variant="outline" size="sm" className="gap-1 text-red-600" onClick={() => changeStatus(viewProposta, 'recusada')}><XCircle className="w-4 h-4" /> Recusada</Button>
                  <Button
                    variant="outline"
                    size="sm"
                    className="gap-1 border-amber-300 text-amber-700 hover:bg-amber-50"
                    data-testid="btn-editar-proposta-enviada"
                    title="Editar proposta já enviada — gera nova versão"
                    onClick={() => { setViewProposta(null); openEdit(viewProposta); }}
                  >
                    <Edit className="w-4 h-4" /> Editar (nova versão)
                  </Button>
                </>
              )}
              {viewProposta.status === 'expirada' && (
                <Button
                  variant="outline"
                  size="sm"
                  className="gap-1 border-amber-300 text-amber-700 hover:bg-amber-50"
                  data-testid="btn-editar-proposta-expirada"
                  title="Reabrir proposta expirada — gera nova versão"
                  onClick={() => { setViewProposta(null); openEdit(viewProposta); }}
                >
                  <Edit className="w-4 h-4" /> Editar (nova versão)
                </Button>
              )}
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Compare Modal */}
      <VersaoCompareModal
        open={!!compareVersion}
        onClose={() => setCompareVersion(null)}
        versaoAtual={{
          versao: versaoAtual,
          itens: viewProposta.itens || [],
          total: viewProposta.total || 0,
          subtotal: viewProposta.subtotal || 0,
          desconto_valor: viewProposta.desconto_valor || 0,
          desconto_calculado: viewProposta.desconto_calculado || 0,
        }}
        versaoAnterior={compareVersion}
      />
    </>
  );
};
